/**
 * PlatformLeaderboardCard — compact top-10 platform-wide leaderboard,
 * embedded on the agency dashboard so institution admins see both the
 * platform ranking (this) AND their own institution's private ranking
 * (AgencyLeaderboard). Item 9 · Feb 2026 spec.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, fmtKES } from "@/lib/api";
import { Trophy, ExternalLink } from "lucide-react";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";

const CRITERIA = [
    { key: "net_pnl",    label: "Net PnL",    testId: "plat-lb-tab-pnl" },
    { key: "win_rate",   label: "Win rate",   testId: "plat-lb-tab-win" },
    { key: "consistency", label: "Consistency", testId: "plat-lb-tab-cons" },
];

export default function PlatformLeaderboardCard() {
    const [criterion, setCriterion] = useState("net_pnl");
    const [rows, setRows]           = useState([]);
    const [loading, setLoading]     = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/leaderboard?criterion=${criterion}`)
            .then(({ data }) => { if (!cancelled) setRows((data.rows || []).slice(0, 10)); })
            .catch(() => { if (!cancelled) toast.error("Failed to load platform leaderboard"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [criterion]);

    return (
        <div className="border border-border bg-card rounded overflow-hidden" data-testid="platform-leaderboard-card">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-primary" />
                    <div className="overline">Platform leaderboard · Top 10</div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
                        {CRITERIA.map((c) => (
                            <button
                                key={c.key}
                                onClick={() => setCriterion(c.key)}
                                data-testid={c.testId}
                                className={"px-2 py-1 " + (
                                    criterion === c.key
                                        ? "bg-primary text-primary-foreground font-semibold"
                                        : "hover:bg-secondary/40"
                                )}
                            >{c.label}</button>
                        ))}
                    </div>
                    <Link
                        to="/app/leaderboard"
                        data-testid="platform-leaderboard-full-link"
                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                    >
                        Full board <ExternalLink className="w-3 h-3" />
                    </Link>
                </div>
            </div>
            {loading ? (
                <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="p-6 text-center text-xs text-muted-foreground">No leaders yet this month.</div>
            ) : (
                <ol className="divide-y divide-border text-sm" data-testid="platform-leaderboard-list">
                    {rows.map((r, i) => (
                        <li key={r.user_id || i} className="px-4 py-2 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <span className={"w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold " + (
                                    i === 0 ? "bg-amber-500/20 text-amber-600" :
                                    i === 1 ? "bg-secondary text-muted-foreground" :
                                    i === 2 ? "bg-orange-500/20 text-orange-600" :
                                              "bg-secondary/60 text-muted-foreground"
                                )}>{i + 1}</span>
                                <div className="min-w-0">
                                    <div className="font-semibold truncate">{r.display_name || r.full_name || "Anonymous"}</div>
                                    <div className="text-[10px] text-muted-foreground">
                                        {r.tier === "moe_institution" ? "MoE" :
                                         r.tier === "cma_institution" ? "CMA" :
                                         "Individual"}
                                    </div>
                                </div>
                            </div>
                            <div className="text-right shrink-0">
                                <MetricValue criterion={criterion} row={r} />
                            </div>
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
}

function MetricValue({ criterion, row }) {
    if (criterion === "net_pnl") {
        const v = Number(row.net_pnl || 0);
        return <span className={`mono font-bold ${toneClassName(signTone(v))}`}>
            {signedPrefix(v)}{fmtKES(v)}
        </span>;
    }
    if (criterion === "win_rate") {
        return <span className="mono font-bold">{Number(row.win_rate || 0).toFixed(1)}%</span>;
    }
    return <span className="mono font-bold">{Number(row.consistency_score || 0).toFixed(2)}</span>;
}
